import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useConnection, useDisconnect, useSwitchChain } from 'wagmi';
import { Link, useLocation } from '../../app/router.tsx';
import { CHAIN } from '../../config/wagmi.ts';
import { useI18n, type MessageKey } from '../../i18n/index.tsx';
import { useIndexer } from '../../hooks/useIndexer.ts';
import { useIsAdmin } from '../../hooks/useIsAdmin.ts';
import { useTimelockOps } from '../../hooks/useTimelockOps.ts';
import { describeCall } from '../../lib/timelock.ts';
import { COMPACT, useMediaQuery } from '../../hooks/useMediaQuery.ts';
import { PERMANENT, useProtocol } from '../../hooks/useProtocol.ts';
import { fmtPct, fmtToken, fmtUsd, shortAddr } from '../../lib/format.ts';
import { Icon, type IconName } from '../ui/Icon.tsx';
import { Logo } from '../ui/Logo.tsx';
import { Button, Countdown, Modal } from '../ui/primitives.tsx';
import { useConnectModal } from './ConnectModal.tsx';
import { LangMenu } from './LangMenu.tsx';
import { EpochBanner } from './EpochBanner.tsx';
import { TestnetBar } from './TestnetBar.tsx';
import { NetworkChip, WalletButton } from './WalletButton.tsx';
import { Coin } from '../ui/coins.tsx';
import { SocialLinks } from '../ui/Social.tsx';

export const NAV: { to: string; key: MessageKey; icon: IconName }[] = [
  { to: '/', key: 'nav.dashboard', icon: 'dashboard' },
  { to: '/stake', key: 'nav.stake', icon: 'stake' },
  { to: '/bond', key: 'nav.bond', icon: 'bond' },
  { to: '/swap', key: 'nav.swap', icon: 'swap' },
  { to: '/liquidity', key: 'nav.liquidity', icon: 'liquidity' },
  { to: '/referral', key: 'nav.referral', icon: 'referral' },
  { to: '/docs', key: 'nav.docs', icon: 'docs' },
  { to: '/admin', key: 'nav.admin', icon: 'admin' },
];
const TABS = ['/', '/stake', '/bond', '/referral'];
/** The Admin entry is listed only while an admin wallet is connected; /admin itself stays reachable by its address. */
const ADMIN_ONLY = '/admin';

function NavLink({ to, k, icon }: { to: string; k: MessageKey; icon: IconName }) {
  const { t } = useI18n();
  return (
    <Link to={to} className="nav-item">
      <Icon name={icon} />
      <span>{t(k)}</span>
    </Link>
  );
}

/** The network card's planet, from the client's design, drawn in 3D: a lit sphere with a glowing magenta core, bands
 *  turning on its surface, and a tilted ring (28 × 9 at −40°) whose far half passes behind the planet and near half in
 *  front of it, with a moon riding the ring (hidden behind the planet, larger in front). viewBox 64. */
function NetIcon() {
  // the ring's frame: x along the ring, y towards the viewer (the near half is y > 32)
  const RING = 'rotate(-40 32 32)';
  const moon = (
    <g className="net-moon-x">
      <g className="net-moon-y">
        <circle cx="32" cy="32" r="2.3" fill="url(#ni-moon)" />
      </g>
    </g>
  );
  return (
    <svg className="net-icon" viewBox="0 0 64 64" width={56} height={56} aria-hidden="true">
      <defs>
        <radialGradient id="ni-halo">
          <stop offset="0" stopColor="#7c3aed" stopOpacity="0.5" />
          <stop offset="1" stopColor="#7c3aed" stopOpacity="0" />
        </radialGradient>
        {/* the sphere, lit from the upper left */}
        <radialGradient id="ni-fill" cx="0.36" cy="0.32" r="0.75">
          <stop offset="0" stopColor="#8f5cf0" />
          <stop offset="0.35" stopColor="#5223c4" />
          <stop offset="0.75" stopColor="#2a0f7a" />
          <stop offset="1" stopColor="#150743" />
        </radialGradient>
        <radialGradient id="ni-core" cx="0.5" cy="0.5" r="0.5">
          <stop offset="0" stopColor="#ffc2fb" />
          <stop offset="0.3" stopColor="#e05cff" />
          <stop offset="0.65" stopColor="#c04dfa" stopOpacity="0.45" />
          <stop offset="1" stopColor="#c04dfa" stopOpacity="0" />
        </radialGradient>
        <radialGradient id="ni-shine" cx="0.5" cy="0.5" r="0.5">
          <stop offset="0" stopColor="#fff" stopOpacity="0.75" />
          <stop offset="1" stopColor="#fff" stopOpacity="0" />
        </radialGradient>
        <radialGradient id="ni-moon" cx="0.35" cy="0.35" r="0.7">
          <stop offset="0" stopColor="#fff" />
          <stop offset="0.5" stopColor="#f2c4ff" />
          <stop offset="1" stopColor="#b065f0" />
        </radialGradient>
        <linearGradient id="ni-line" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#f0d4ff" />
          <stop offset="0.5" stopColor="#c48cf0" />
          <stop offset="1" stopColor="#6b3fc4" />
        </linearGradient>
        <linearGradient id="ni-ring" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#8a5bd6" />
          <stop offset="0.5" stopColor="#f0d0ff" />
          <stop offset="1" stopColor="#8a5bd6" />
        </linearGradient>
        <clipPath id="ni-far">
          <rect x="-8" y="-8" width="80" height="40" />
        </clipPath>
        <clipPath id="ni-near">
          <rect x="-8" y="32" width="80" height="40" />
        </clipPath>
        <clipPath id="ni-ball">
          <circle cx="32" cy="32" r="14.5" />
        </clipPath>
        <filter id="ni-glow" x="-30%" y="-30%" width="160%" height="160%">
          <feGaussianBlur stdDeviation="1.6" result="b" />
          <feMerge>
            <feMergeNode in="b" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>
      <circle className="net-halo" cx="32" cy="32" r="26" fill="url(#ni-halo)" />
      <g className="net-float">
        {/* behind the planet: the far half of the ring and the moon while it is there */}
        <g transform={RING} filter="url(#ni-glow)">
          <g clipPath="url(#ni-far)" opacity="0.6">
            <ellipse cx="32" cy="32" rx="28" ry="9" fill="none" stroke="#a974e6" strokeWidth="1.8" />
            {moon}
          </g>
        </g>
        {/* the planet */}
        <g filter="url(#ni-glow)">
          <circle cx="32" cy="32" r="14.5" fill="url(#ni-fill)" />
        </g>
        <g clipPath="url(#ni-ball)">
          <circle className="net-core" cx="32" cy="32" r="9" fill="url(#ni-core)" />
          <g transform={RING} fill="none" strokeLinecap="round">
            <ellipse className="net-band" cx="32" cy="27" rx="15" ry="4.2" stroke="#c9a2ff" strokeOpacity="0.35" strokeWidth="1.1" strokeDasharray="7 5" />
            <ellipse className="net-band slow" cx="32" cy="37.5" rx="14" ry="3.6" stroke="#e7b6ff" strokeOpacity="0.25" strokeWidth="1" strokeDasharray="4 6" />
          </g>
          {/* the night side, away from the light */}
          <circle cx="40" cy="40" r="17" fill="#0b0326" opacity="0.38" />
          <ellipse cx="26" cy="24.5" rx="5" ry="3.2" transform="rotate(-35 26 24.5)" fill="url(#ni-shine)" />
        </g>
        <circle cx="32" cy="32" r="14.5" fill="none" stroke="url(#ni-line)" strokeWidth="2.2" filter="url(#ni-glow)" />
        {/* in front of the planet: the near half of the ring and the moon while it is there */}
        <g transform={RING} filter="url(#ni-glow)">
          <g clipPath="url(#ni-near)">
            <ellipse cx="32" cy="32" rx="28" ry="9" fill="none" stroke="url(#ni-ring)" strokeWidth="2.6" />
            {moon}
          </g>
        </g>
      </g>
    </svg>
  );
}

/** Live network status, the current epoch and the time to the next rebase. */
function NetworkCard() {
  const { t } = useI18n();
  const { data } = useProtocol();
  const { chainId, isConnected } = useConnection();
  const { switchChain } = useSwitchChain();
  const wrong = isConnected && chainId !== CHAIN.id;
  return (
    <div className="side-card" data-testid="network-card">
      <div className="net-head">
        <NetIcon />
        <div>
          <div className="soft">{CHAIN.name}</div>
          {wrong ? (
            <button type="button" className="link-btn net-status bad" onClick={() => switchChain({ chainId: CHAIN.id })}>
              {t('network.wrong')}
            </button>
          ) : (
            <div className="net-status">{t('side.active')}</div>
          )}
        </div>
      </div>
      <div className="soft">
        {t('side.epoch')} <b className="num">{data.epoch ?? '—'}</b>
      </div>
      <div className="soft">
        {t('side.endsIn')} <b>{data.nextEpochAt ? <Countdown to={data.nextEpochAt} /> : '—'}</b>
      </div>
      <SocialLinks className="side-social" />
    </div>
  );
}

/** The connected wallet (or the connect button), at the right of the top bar on desktop. */
function WalletCard() {
  const { t } = useI18n();
  const { address, isConnected } = useConnection();
  const { disconnect } = useDisconnect();
  const open = useConnectModal();
  const [menu, setMenu] = useState(false);
  const [copied, setCopied] = useState(false);
  if (!isConnected || !address) {
    return (
      <Button onClick={open}>
        <Icon name="wallet" width={18} height={18} />
        {t('wallet.connect')}
      </Button>
    );
  }
  return (
    <div style={{ position: 'relative' }}>
      <button
        type="button"
        className="wallet-top"
        onClick={() => setMenu((m) => !m)}
        aria-haspopup="menu"
        aria-expanded={menu}
        data-testid="wallet-chip"
      >
        <span className="avatar" aria-hidden="true">
          0x
        </span>
        <span className="who">
          <b className="num">{shortAddr(address)}</b>
          <span>
            <span className="dot-ok" />
            {t('wallet.connected')}
          </span>
        </span>
        <Icon name="chevron" width={18} height={18} />
      </button>
      {menu ? (
        <div className="wallet-menu" role="menu">
          <Button
            size="sm"
            variant="ghost"
            onClick={async () => {
              await navigator.clipboard?.writeText(address).catch(() => undefined);
              setCopied(true);
              setTimeout(() => setCopied(false), 1500);
            }}
          >
            {copied ? t('wallet.copied') : t('wallet.copy')}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              disconnect();
              setMenu(false);
            }}
          >
            {t('wallet.disconnect')}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

interface Prices {
  change24h: number | null;
}

/** SYN at the 30-minute average, with the 24 h change once the indexer has a day of history. */
function PriceChip() {
  const { t } = useI18n();
  const { data } = useProtocol();
  const prices = useIndexer<Prices>('/prices?hours=26', 60_000);
  const change = prices.data?.change24h;
  return (
    <div className="price-chip" data-testid="price-chip">
      <Coin token="SYN" size={30} />
      <div>
        <div className="lbl">{t('price.label')}</div>
        <div className="val">
          <span className="num">{fmtUsd(data.price)}</span>
          {typeof change === 'number' ? (
            <span className={`change ${change > 0 ? 'up' : change < 0 ? 'down' : 'flat'}`}>
              {change !== 0 ? <Icon name={change > 0 ? 'up' : 'down'} width={13} height={13} style={{ verticalAlign: -2 }} /> : null}{' '}
              {fmtPct(Math.abs(change), 2)}
            </span>
          ) : (
            <span className="change flat">{t('price.twap')}</span>
          )}
        </div>
      </div>
    </div>
  );
}

interface ActivityRow {
  id: string;
  kind: string;
  amount: string | null;
  timestamp: string;
}

const SEEN_KEY = 'syntra.notes.seen';

/** Notifications: the next rebase, the current rate and (with the indexer) the wallet's latest activity. */
function Notifications() {
  const { t } = useI18n();
  const { data } = useProtocol();
  const { address } = useConnection();
  const activity = useIndexer<ActivityRow[]>(address ? `/activity/${address}?limit=5` : null, 20_000);
  // governance notices for everyone: what is scheduled (with its time) and what ran, newest first
  const { ops } = useTimelockOps();
  const changes = ops.filter((o) => o.state === 1 || o.state === 2 || o.state === 3).slice(-5).reverse();
  const [open, setOpen] = useState(false);
  const [seen, setSeen] = useState(() => {
    try {
      return Number(localStorage.getItem(SEEN_KEY) ?? 0);
    } catch {
      return 0;
    }
  });
  const ref = useRef<HTMLDivElement>(null);
  const rows = activity.data ?? [];
  const newest = rows.length ? Number(rows[0].timestamp) : 0;
  const unread = newest > seen;
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => ref.current && !ref.current.contains(e.target as Node) && setOpen(false);
    window.addEventListener('mousedown', close);
    return () => window.removeEventListener('mousedown', close);
  }, [open]);
  const toggle = () => {
    setOpen((o) => !o);
    if (newest > seen) {
      setSeen(newest);
      try {
        localStorage.setItem(SEEN_KEY, String(newest));
      } catch {
        /* storage blocked */
      }
    }
  };
  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button type="button" className="icon-btn bell" aria-label={t('notes.title')} aria-expanded={open} onClick={toggle}>
        <Icon name="bell" />
        {unread ? <span className="badge" /> : null}
      </button>
      {open ? (
        <div className="popover" role="dialog" aria-label={t('notes.title')}>
          <h4>{t('notes.title')}</h4>
          <ul className="note-list">
            <li>
              <span className="soft">{t('notes.nextRebase', { n: data.epoch ?? '—' })}</span>
              <b>{data.nextEpochAt ? <Countdown to={data.nextEpochAt} /> : '—'}</b>
            </li>
            <li>
              <span className="soft">{t('notes.rate')}</span>
              <b className="num">
                {data.plans ? `${fmtPct(Number(data.plans[0].rate) / 1e18, 3)} – ${fmtPct(Number(data.plans[PERMANENT].rate) / 1e18, 3)}` : '—'}
              </b>
            </li>
            {changes.map((o) => (
              <li key={o.id} data-testid="note-change">
                <span>
                  <span className="soft">{t(o.state === 3 ? 'notes.changeDone' : 'notes.change')}</span>
                  <br />
                  {describeCall(o.calls[0].target, o.calls[0].data, t)}
                  {o.calls.length > 1 ? ` + ${t('admin.moreCalls', { n: o.calls.length - 1 })}` : ''}
                </span>
                <span className="muted num">{o.state === 3 ? t('admin.done') : o.timestamp > 1 ? new Date(o.timestamp * 1000).toLocaleDateString() : ''}</span>
              </li>
            ))}
            {rows.map((a) => (
              <li key={a.id}>
                <span>{t(`act.${a.kind}` as MessageKey)}</span>
                <span className="muted num">
                  {a.amount ? `${fmtToken(BigInt(a.amount))} SYN · ` : ''}
                  {new Date(Number(a.timestamp) * 1000).toLocaleDateString()}
                </span>
              </li>
            ))}
            {address && activity.configured && rows.length === 0 ? <li className="muted">{t('notes.empty')}</li> : null}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

export function Shell({ title, subtitle, children }: { title: string; subtitle?: string; children: ReactNode }) {
  const { t } = useI18n();
  const { path } = useLocation();
  const [more, setMore] = useState(false);
  const compact = useMediaQuery(COMPACT);
  const inMore = !TABS.includes(path);
  const { admin } = useIsAdmin();
  const nav = admin ? NAV : NAV.filter((n) => n.to !== ADMIN_ONLY);
  return (
    <div className="shell">
      <nav className="sidebar" aria-label="main">
        <Link to="/" className="brand" aria-label="Syntra DAO">
          {/* the client's own artwork: the full lockup, or just the mark when the sidebar collapses to icons */}
          <Logo variant="lockup" size={204} className="brand-lockup" />
          <Logo variant="mark" size={46} className="brand-mark" />
        </Link>
        {nav.map((n) => (
          <NavLink key={n.to} to={n.to} k={n.key} icon={n.icon} />
        ))}
        <div className="spacer" />
        {compact ? null : <NetworkCard />}
      </nav>
      <header className="topbar">
        <Logo variant="mark" size={32} alt="" className="mobile-logo" />
        <div className="title-block">
          <h1>{title}</h1>
          {subtitle ? <p>{subtitle}</p> : null}
        </div>
        <div className="grow" />
        <span className="desktop-only">
          <PriceChip />
        </span>
        <LangMenu />
        <span className="desktop-only">
          <Notifications />
        </span>
        {compact ? (
          <>
            <NetworkChip />
            <WalletButton />
          </>
        ) : (
          <WalletCard />
        )}
      </header>
      <main className="main">
        <div className="page">
          <TestnetBar />
          <EpochBanner />
          {children}
        </div>
      </main>
      <nav className="tabbar" aria-label="tabs">
        {nav.filter((n) => TABS.includes(n.to)).map((n) => (
          <NavLink key={n.to} to={n.to} k={n.key} icon={n.icon} />
        ))}
        <button type="button" className="nav-item" aria-current={inMore ? 'page' : undefined} onClick={() => setMore(true)} style={{ border: 0, background: 'none' }}>
          <Icon name="more" />
          <span>{t('nav.more')}</span>
        </button>
      </nav>
      {more ? (
        <Modal title={t('nav.more')} onClose={() => setMore(false)}>
          <div className="wallet-list">
            {nav.filter((n) => !TABS.includes(n.to)).map((n) => (
              <Link key={n.to} to={n.to} className="wallet-option" onClick={() => setMore(false)} style={{ textDecoration: 'none' }}>
                <Icon name={n.icon} width={20} height={20} />
                {t(n.key)}
              </Link>
            ))}
          </div>
          <SocialLinks className="more-social" />
        </Modal>
      ) : null}
    </div>
  );
}
